import { useState, useEffect } from 'react';

/**
 * Custom hook for responsive design utilities
 * Provides breakpoint detection and device information
 */
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  const [orientation, setOrientation] = useState(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight 
      ? 'landscape' 
      : 'portrait'
  );

  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    const updateScreenInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setScreenSize({ width, height });
      setOrientation(width > height ? 'landscape' : 'portrait');
      
      // Determine device type based on screen size and user agent
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      if (width <= 480) {
        setDeviceType('mobile');
      } else if (width <= 768) {
        setDeviceType(isTouchDevice ? 'tablet' : 'desktop');
      } else if (width <= 1024) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
    };

    // Initial call
    updateScreenInfo();

    // Event listeners
    window.addEventListener('resize', updateScreenInfo);
    window.addEventListener('orientationchange', updateScreenInfo);

    return () => {
      window.removeEventListener('resize', updateScreenInfo);
      window.removeEventListener('orientationchange', updateScreenInfo);
    };
  }, []);

  // Breakpoint helpers
  const breakpoints = {
    mobile: screenSize.width <= 480,
    tablet: screenSize.width > 480 && screenSize.width <= 768,
    desktop: screenSize.width > 768,
    large: screenSize.width > 1024,
    xlarge: screenSize.width > 1200,
  };

  // Utility functions
  const isMobile = breakpoints.mobile;
  const isTablet = breakpoints.tablet;
  const isDesktop = breakpoints.desktop;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isLandscape = orientation === 'landscape';
  const isPortrait = orientation === 'portrait';

  // Grid column calculator
  const getGridColumns = (mobileColumns = 1, tabletColumns = 2, desktopColumns = 3) => {
    if (isMobile) return mobileColumns;
    if (isTablet) return tabletColumns;
    return desktopColumns;
  };

  // Responsive padding/margin calculator
  const getResponsiveSpacing = (mobile = 16, tablet = 24, desktop = 32) => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  // Font size calculator
  const getResponsiveFontSize = (mobile = 14, tablet = 16, desktop = 18) => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  return {
    screenSize,
    orientation,
    deviceType,
    breakpoints,
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    isLandscape,
    isPortrait,
    getGridColumns,
    getResponsiveSpacing,
    getResponsiveFontSize,
  };
};

/**
 * Hook for detecting if user prefers reduced motion
 */
export const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * Hook for detecting color scheme preference
 */
export const useColorSchemePreference = () => {
  const [prefersDark, setPrefersDark] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setPrefersDark(mediaQuery.matches);

    const handleChange = (event) => {
      setPrefersDark(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersDark;
};

export default useResponsive;






















